import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { statusPagesApi } from '../api'
import type { PublicStatusPageResponse } from '../types'
import { CheckCircle, AlertTriangle, XCircle, HelpCircle, Clock } from 'lucide-react'

export default function PublicStatusPage() {
  const { slug } = useParams<{ slug: string }>()
  const [data, setData] = useState<PublicStatusPageResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (slug) {
      loadStatusPage()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  const loadStatusPage = async () => {
    try {
      const response = await statusPagesApi.getPublic(slug!)
      setData(response)
    } catch {
      setError('Status page not found')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Status Page Not Found</h1>
          <p className="text-slate-600">The requested status page does not exist.</p>
        </div>
      </div>
    )
  }

  const { status_page, current_status, uptime_stats, response_time_data, incident_history } = data

  const getStatusIcon = () => {
    switch (current_status.status) {
      case 'operational':
        return <CheckCircle className="w-8 h-8 text-green-500" />
      case 'degraded':
        return <AlertTriangle className="w-8 h-8 text-yellow-500" />
      case 'down':
        return <XCircle className="w-8 h-8 text-red-500" />
      default:
        return <HelpCircle className="w-8 h-8 text-slate-500" />
    }
  }

  const getStatusColor = () => {
    switch (current_status.status) {
      case 'operational':
        return 'text-green-500'
      case 'degraded':
        return 'text-yellow-500'
      case 'down':
        return 'text-red-500'
      default:
        return 'text-slate-500'
    }
  }

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: status_page.background_color }}>
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          {status_page.logo_url && (
            <img src={status_page.logo_url} alt={status_page.title} className="h-12 mx-auto mb-4" />
          )}
          <h1 className="text-3xl font-bold text-slate-900 mb-2">{status_page.title}</h1>
          {status_page.description && <p className="text-slate-600">{status_page.description}</p>}
        </div>

        {/* Current Status */}
        <div className="bg-slate-900/50 border border-slate-200 rounded-xl p-8 mb-8">
          <div className="flex items-center justify-center gap-4">
            {getStatusIcon()}
            <div className="text-center">
              <h2 className={`text-2xl font-bold capitalize ${getStatusColor()}`}>
                {current_status.status === 'operational'
                  ? 'All Systems Operational'
                  : current_status.message}
              </h2>
              {current_status.last_checked && (
                <p className="text-sm text-slate-500 mt-1">
                  Last checked: {new Date(current_status.last_checked).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Uptime Stats */}
        {uptime_stats && (
          <div className="bg-slate-900/50 border border-slate-200 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">Uptime</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(uptime_stats).map(([period, value]) => (
                <div key={period} className="text-center">
                  <div className="text-2xl font-bold text-slate-900">
                    {value !== null ? `${value.toFixed(2)}%` : 'N/A'}
                  </div>
                  <div className="text-sm text-slate-400">{period}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Response Time Chart */}
        {response_time_data && response_time_data.length > 0 && (
          <div className="bg-slate-900/50 border border-slate-200 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">Response Time (24h)</h3>
            <div className="h-40 flex items-end gap-1">
              {response_time_data.slice(-48).map((point, i) => {
                const maxTime = Math.max(...response_time_data.map(p => p.response_time_ms))
                const height = (point.response_time_ms / maxTime) * 100
                return (
                  <div
                    key={i}
                    className="flex-1 rounded-t transition-all hover:opacity-80"
                    style={{
                      height: `${height}%`,
                      backgroundColor: status_page.primary_color,
                      minHeight: '4px',
                    }}
                    title={`${point.response_time_ms}ms at ${new Date(point.timestamp).toLocaleString()}`}
                  />
                )
              })}
            </div>
            <div className="flex justify-between mt-2 text-xs text-slate-500">
              <span>24h ago</span>
              <span>Now</span>
            </div>
          </div>
        )}

        {/* Incident History */}
        {incident_history && incident_history.length > 0 && (
          <div className="bg-slate-900/50 border border-slate-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Incident History</h3>
            <div className="space-y-4">
              {incident_history.map(incident => (
                <div
                  key={incident.id}
                  className="border-l-2 pl-4 py-2"
                  style={{
                    borderColor: incident.is_resolved ? '#10b981' : '#ef4444',
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`text-sm font-medium ${incident.is_resolved ? 'text-green-400' : 'text-red-400'}`}
                    >
                      {incident.is_resolved ? 'Resolved' : 'Ongoing'}
                    </span>
                    {incident.duration_seconds && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDuration(incident.duration_seconds)}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-300 capitalize">
                    {incident.cause.replace('_', ' ')}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {new Date(incident.started_at).toLocaleString()}
                    {incident.ended_at && ` - ${new Date(incident.ended_at).toLocaleString()}`}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 text-sm text-slate-500">
          Powered by{' '}
          <a
            href="/"
            className="hover:text-white transition-colors"
            style={{ color: status_page.primary_color }}
          >
            Falcon
          </a>
        </div>
      </div>
    </div>
  )
}
